import React from "react";
import { Download, Trash2, FileText, ReceiptText, CheckCircle2, Pencil, Printer, ChevronDown } from "lucide-react";
import { inr } from "../../lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { MultiSelect } from "../MultiSelect";
import { LockBadge } from "../ApprovalLock";
import { downloadDocPdf } from "./shared";
import { AttachButton } from "../Attachments";

const COLS = "grid grid-cols-[auto_auto_1fr_auto_auto_auto] gap-4 px-5";
const methodColors = {
  cash: "bg-accent/15 text-accent", bank: "bg-primary/10 text-primary",
  upi: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]", qr: "bg-destructive/10 text-destructive", split: "bg-muted text-foreground",
};
const methodMix = (d) => (d.splits && d.splits.length > 1)
  ? d.splits.map((s) => `₹${Number(s.amount).toLocaleString("en-IN")} ${s.method === "cash" ? "cash" : s.method.toUpperCase()}`).join(" · ")
  : "";
const dateCtl = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";
const barBtn = "h-9 px-3 rounded-md border bg-card text-sm flex items-center gap-1.5 transition-colors";

export const DocFilters = ({ tab, setTab, f, setF, partyOpts }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
    <div className="inline-flex rounded-lg border border-border bg-card p-1">
      {[["voucher", "Vouchers", FileText], ["receipt", "Payment Receipts", ReceiptText]].map(([k, lbl, Icon]) => (
        <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
          className={`px-4 h-9 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <Icon size={15} /> {lbl}
        </button>
      ))}
    </div>
    <div className="flex items-end gap-2 flex-wrap">
      <MultiSelect label="Parties" testid="voucher-party-filter" options={partyOpts} selected={f.parties} onChange={(v) => setF({ ...f, parties: v })} placeholder="All parties" />
      <div><div className="text-[11px] text-muted-foreground mb-0.5">From</div><input data-testid="voucher-start" type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={dateCtl} /></div>
      <div><div className="text-[11px] text-muted-foreground mb-0.5">To</div><input data-testid="voucher-end" type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={dateCtl} /></div>
      {(f.start || f.end || f.parties.length > 0) && (
        <button data-testid="voucher-clear-range" onClick={() => setF({ start: "", end: "", parties: [] })} className="h-9 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
      )}
    </div>
  </div>
);

export const DocBulkBar = ({ count, canWrite, onExport, onDelete, onClear }) => {
  if (count === 0) return null;
  return (
    <div data-testid="voucher-bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
      <span className="text-sm font-medium">{count} selected</span>
      <div className="flex-1" />
      <button data-testid="voucher-bulk-export" onClick={onExport} className={`${barBtn} border-input hover:bg-muted`}><Download size={14} /> Export CSV</button>
      {canWrite && <button data-testid="voucher-bulk-del" onClick={onDelete} className={`${barBtn} border-destructive/30 text-destructive hover:bg-destructive/10`}><Trash2 size={14} /> Delete selected</button>}
      <button data-testid="voucher-bulk-clear" onClick={onClear} className={`${barBtn} border-input hover:bg-muted`}>Clear</button>
    </div>
  );
};

const PdfMenu = ({ d }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid={`download-doc-${d.id}`} className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
        <Download size={14} /> PDF <ChevronDown size={13} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <DropdownMenuLabel className="text-xs">A4 page</DropdownMenuLabel>
      <DropdownMenuItem data-testid={`doc-a4-download-${d.id}`} onClick={() => downloadDocPdf(d.id, d.number, "a4", "download")}><Download size={14} className="mr-2" /> Download A4</DropdownMenuItem>
      <DropdownMenuItem data-testid={`doc-a4-print-${d.id}`} onClick={() => downloadDocPdf(d.id, d.number, "a4", "print")}><Printer size={14} className="mr-2" /> Print A4</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs">80mm thermal (3")</DropdownMenuLabel>
      <DropdownMenuItem data-testid={`doc-thermal-download-${d.id}`} onClick={() => downloadDocPdf(d.id, d.number, "thermal", "download")}><Download size={14} className="mr-2" /> Download 80mm</DropdownMenuItem>
      <DropdownMenuItem data-testid={`doc-thermal-print-${d.id}`} onClick={() => downloadDocPdf(d.id, d.number, "thermal", "print")}><Printer size={14} className="mr-2" /> Print 80mm</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const DocInfo = ({ d }) => (
  <div className="min-w-0">
    <div className="font-medium break-words">{d.receiver_name}</div>
    {d.account_name ? <div className="text-xs text-accent break-words">A/c: {d.account_name}</div> : null}
    <div className="text-xs text-muted-foreground num truncate">
      {d.date}{d.voucher_type ? ` · ${d.voucher_type}` : ""}{d.employee_name ? ` · ${d.employee_name}` : ""}{d.mobile ? ` · ${d.mobile}` : ""}
      {d.has_cashier_sign && d.has_receiver_sign && <span className="inline-flex items-center gap-0.5 ml-1 text-[hsl(var(--positive))]"><CheckCircle2 size={11} /> signed</span>}
    </div>
    {methodMix(d) && <span data-testid={`doc-mix-${d.id}`} className="inline-block mt-1 text-[10px] num bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{methodMix(d)}</span>}
  </div>
);

const DocRow = ({ d, checked, onToggle, lock, onLock, canWrite, onEdit, onDelete, onAttach }) => (
  <div data-testid={`doc-row-${d.id}`} className={`${COLS} py-4 items-center hover:bg-background/50 transition-colors ${checked ? "bg-accent/5" : ""}`}>
    <input data-testid={`voucher-select-${d.id}`} type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 accent-[hsl(var(--primary))]" />
    <span className="num text-sm font-bold text-primary flex flex-col items-start gap-0.5">{d.number}{lock && <LockBadge info={lock} testid={`doc-lock-${d.id}`} onClick={onLock} />}</span>
    <div className="cursor-pointer min-w-0" onClick={() => onAttach(d)} title="Open attachments"><DocInfo d={d} /></div>
    <span className="num text-sm font-bold text-right">{inr(d.amount)}</span>
    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded num font-bold text-center ${methodColors[d.pay_method]}`}>{d.pay_method}</span>
    <div className="flex items-center gap-2 justify-end">
      <AttachButton testid={`att-doc-${d.id}`} count={d.attachment_count} onClick={() => onAttach(d)} />
      <PdfMenu d={d} />
      {canWrite && <button data-testid={`edit-doc-${d.id}`} onClick={() => (lock ? onLock() : onEdit(d))} title="Edit" className="text-muted-foreground hover:text-primary transition-colors"><Pencil size={15} /></button>}
      {canWrite && <button data-testid={`del-doc-${d.id}`} onClick={() => (lock ? onLock() : onDelete(d))} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>}
    </div>
  </div>
);

export const DocTable = ({ tab, docs, selected, toggleSel, allSelected, toggleAll, locks, setLockInfo, canWrite, onEdit, onDelete, onAttach }) => {
  if (docs.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
        {tab === "voucher" ? <FileText className="mx-auto mb-3 opacity-40" size={32} /> : <ReceiptText className="mx-auto mb-3 opacity-40" size={32} />}
        No {tab === "voucher" ? "vouchers" : "receipts"} yet.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
      <div className={`${COLS} py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
        <input data-testid="voucher-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" />
        <span>No.</span><span>{tab === "voucher" ? "Paid To / Type" : "Received From"}</span><span className="text-right">Amount</span><span>Mode</span><span></span>
      </div>
      <div className="divide-y divide-border">
        {docs.map((d) => (
          <DocRow key={d.id} d={d} checked={selected.has(d.id)} onToggle={() => toggleSel(d.id)} lock={locks[d.id]} onLock={() => setLockInfo(locks[d.id])}
            canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} onAttach={onAttach} />
        ))}
      </div>
    </div>
  );
};
